import { invalidateNotificationQueries } from "@/lib/mobile-notifications";
import { useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetNotifications,
  apiMarkNotificationRead,
  apiMarkAllNotificationsRead,
  type AppNotification
} from "@/lib/api";
import { formatDistanceToNow, type Locale } from "date-fns";
import { Button } from "@/components/ui/button";
import { CheckCheck, Bell, ChevronLeft, ChevronRight } from "lucide-react";
import { NotificationIcon } from "@/components/notifications/NotificationIcon";
import { getNotificationDisplayText, isMedicalCheckAssignmentNotification } from "@/lib/notification-display";
import { useDateLocale } from "@/hooks/useDateLocale";
import { getTimeOffNotificationPath } from "@/lib/notification-routing";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ListSkeleton } from "@/components/PageSkeleton";

// ── Helpers ───────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function timeAgo(dateStr: string, locale: Locale) {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale });
  } catch {
    return "";
  }
}


export default function Notifications() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOrHr = user?.role?.mainRole === "admin" || user?.role?.mainRole === "hr";
  const canAccessTeamLeave = isAdminOrHr || !!(user?.isLineManager || user?.role?.subRole === "line_manager");
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["notifications", "page", page],
    queryFn: () => apiGetNotifications(PAGE_SIZE, (page - 1) * PAGE_SIZE),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => apiMarkNotificationRead(id),
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });

  const markAllMutation = useMutation({
    mutationFn: apiMarkAllNotificationsRead,
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });

  function handleReadAndNavigate(
    id: number,
    entityId: number | null,
    entityType: string,
    notifType: string,
  ) {
    if (data?.notifications.find((n) => n.id === id && !n.isRead)) {
      markReadMutation.mutate(id);
    }

    if (notifType === "salary_processed") {
      navigate("/my-salary");
    } else if (notifType === "salary_increase_approved" || notifType === "salary_increase_rejected") {
      navigate("/my-tasks?tab=submitted");
    } else if (notifType === "salary_increase_requested" || notifType === "workflow_task" || entityType === "workflow_task") {
      navigate(entityId ? `/my-tasks?taskId=${entityId}` : "/my-tasks");
    } else if (notifType === "event_new") {
      navigate("/corporate-calendar");
    } else if (entityType === "document") {
      navigate(`/documents${entityId ? `?openDocument=${entityId}` : ''}`);
    } else if (entityType === "time_off") {
      navigate(getTimeOffNotificationPath(notifType, entityId, canAccessTeamLeave));
    } else if (notifType === "onboarding_started" || entityType === "onboarding_session") {
      navigate(`/my-onboarding${entityId ? `?sessionId=${entityId}` : ""}`);
    } else if (entityType === "onboarding" || entityType === "messages") {
      navigate(`/messages${entityId ? `?with=${entityId}` : ""}`);
    } else if (entityType === "concern_case") {
      if (notifType === "concern_new" || notifType === "concern_assigned") {
        navigate(`/hr-cases${entityId ? `?caseId=${entityId}` : ""}`);
      } else {
        navigate(`/raise-concern${entityId ? `?caseId=${entityId}` : ""}`);
      }
    } else if (entityType === "survey") {
      navigate("/my-surveys");
    } else if (entityType === "expense") {
      if (notifType === "expense_submitted") {
        navigate(`/expenses${entityId ? `?expense=${entityId}` : ""}`);
      } else {
        navigate(`/my-expenses${entityId ? `?expense=${entityId}` : ""}`);
      }
    } else if (entityType === "offboarding") {
      navigate(`/team-overview${entityId ? `?openOffboarding=${entityId}` : ''}`);
    } else if (entityType === "recruitment_application") {
      navigate(`/recruitment${entityId ? `?tab=applications&applicationId=${entityId}` : "?tab=applications"}`);
    } else if (entityType === "recruitment_position") {
      navigate(`/recruitment${entityId ? `?tab=positions&positionId=${entityId}` : "?tab=positions"}`);
    } else if (notifType === "team_join_request") {
      navigate("/teams-management?tab=requests");
    } else if (notifType === "team_request_approved" || notifType === "team_request_rejected") {
      navigate("/team-overview");
    } else if (entityType === "medical_check") {
      const assignmentLink = entityId && isMedicalCheckAssignmentNotification(notifType);
      const legacy = notifType === "medical_check_confirmed" ? "&legacyEmployeeId=true" : "";
      if (isAdminOrHr) {
        navigate(`/medical-checkups${assignmentLink ? `?assignment=${entityId}${legacy}` : ""}`);
      } else {
        navigate(`/${assignmentLink ? `?assignmentId=${entityId}` : ""}`);
      }
    }
  }

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <PageHeader
          title={t("notifications.title", "Notifications")}
          subtitle={t("notifications.subtitle", "Stay updated on activities and tasks")}
          icon={Bell}
          iconColor="text-blue-600"
          iconBg="bg-blue-500/10"
          action={
            unreadCount > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
                className="gap-2"
              >
                <CheckCheck className="h-4 w-4" />
                {t("notifications.markAllRead", "Mark All as Read")}
              </Button>
            ) : undefined
          }
        />

        <Card className="shadow-card">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4">
                <ListSkeleton rows={6} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground flex flex-col items-center">
                <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center mb-4">
                  <CheckCheck className="h-8 w-8 text-muted-foreground/50" />
                </div>
                <h3 className="text-base font-medium text-foreground mb-1">
                  {t("notifications.caughtUp", "You're all caught up!")}
                </h3>
                <p className="text-sm">
                  {t("notifications.noNotifications", "You don't have any notifications right now.")}
                </p>
              </div>
            ) : (
              <>
                <div className={`divide-y divide-border transition-opacity ${isFetching ? "opacity-60" : ""}`}>
                  {notifications.map((n) => {
                    const display = getNotificationDisplayText(n, t);

                    return (
                      <button
                        key={n.id}
                        onClick={() => handleReadAndNavigate(n.id, n.entityId ?? null, n.entityType, n.type)}
                        className={`w-full text-left p-4 hover:bg-accent/50 transition-colors flex items-start gap-4 cursor-pointer ${
                          !n.isRead ? "bg-primary/5" : ""
                        }`}
                      >
                        <NotificationIcon type={n.type} size="md" dimmed={n.isRead} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className={`text-sm font-medium truncate ${!n.isRead ? "text-foreground" : "text-muted-foreground"}`}>
                              {display.title}
                            </p>
                            <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                              {timeAgo(n.createdAt, dateLocale)}
                            </span>
                          </div>

                          <p className={`text-sm line-clamp-2 ${!n.isRead ? "text-foreground/90" : "text-muted-foreground/80"}`}>
                            {display.message}
                          </p>
                        </div>

                        {!n.isRead && (
                          <div className="shrink-0 flex self-center">
                             <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Pagination bar */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      {rangeStart}–{rangeEnd} of {total}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={page <= 1 || isFetching}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <span className="text-xs text-muted-foreground px-2 tabular-nums">
                        {page} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={page >= totalPages || isFetching}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
